import { Component, Input, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonService } from '../../../services/Common-function/common.service';
import { interval, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AnimationOptions } from 'ngx-lottie';
import { PlatformSaleService } from 'src/app/services/platform-sale/platform-sale.service';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'ipm-diwali-section',
  templateUrl: './diwali-section.component.html',
  styleUrls: ['./diwali-section.component.scss']
})
export class DiwaliSectionComponent implements OnInit, OnDestroy {
  @Input() showSection: boolean = true;
  @Input() extendBackground: boolean = true;
  @Input() saleEndDate: string = '2025-10-21T18:59:59';
  type: string = 'diwali';
  @Input() saleStartDate: string;
   optionsBpm: AnimationOptions = {
      path: '/assets/data/bpmtext.json',
      loop: true,
      autoplay: true
    };

  countdown: CountdownTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  nextCountdown: CountdownTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };

  salePhase: 'before' | 'during' | 'ended' = 'before';
  private saleStartDateObj?: Date;
  private saleEndDateObj?: Date;
  private hasShownStartAlert = false;
  private hasShownEndAlert = false;

  flipStates = {
    days: false,
    hours: false,
    minutes: false,
    seconds: false
  };
  isSaleActive: boolean = false;

  // Constants for animation timing
  private readonly FLIP_ANIMATION_DELAY = 400;
  private readonly FLIP_ANIMATION_DURATION = 500;

  private destroy$ = new Subject<void>();

  constructor(
    public commonService: CommonService,
    private elementRef: ElementRef,
    private platformSaleService: PlatformSaleService
  ) { }

  ngOnInit(): void {
    this.commonService.platformSaleData.subscribe((res: any) => {
      if (res) {
        let activeSaleData = this.commonService.getActivePlatformSale(res);
        if(activeSaleData) {
          // live between start_at and end_at (from the platformSaleData API)
          this.isSaleActive = this.commonService.saleActive;
          this.saleStartDate = activeSaleData.start_at || this.saleStartDate;
          this.saleEndDate = activeSaleData.end_at ? activeSaleData.end_at : "2025-10-21T20:59:59";
        } else {
          this.saleStartDate = '2025-10-21T17:59:59';
          this.saleEndDate = '2025-10-21T20:59:59';
          this.isSaleActive = false;
        }
         this.initializeCountdown();
      }
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('diwali-background-active');
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeCountdown(): void {
    try {
      this.saleStartDateObj = new Date(this.saleStartDate);
      this.saleEndDateObj = new Date(this.saleEndDate);

      if (isNaN(this.saleStartDateObj.getTime()) || isNaN(this.saleEndDateObj.getTime())) {
        console.error('Invalid sale dates provided');
        return;
      }

      this.updateCountdown();

      interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateCountdown();
        });
    } catch (error) {
      console.error('Error initializing countdown:', error);
    }
  }

  private updateCountdown(): void {
    if (!this.saleStartDateObj || !this.saleEndDateObj) return;

    const now = new Date().getTime();
    const startTime = this.saleStartDateObj.getTime();
    const endTime = this.saleEndDateObj.getTime();

    let distance: number;
    let newPhase: 'before' | 'during' | 'ended';
    const previousPhase = this.salePhase;

    if (now < startTime) {
      // Phase 1: Before sale starts
      newPhase = 'before';
      distance = startTime - now;
    } else if (now >= startTime && now < endTime) {
      // Phase 2: Sale is active
      newPhase = 'during';
      distance = endTime - now;

      // Show start alert only once when transitioning from 'before' to 'during'
      if (previousPhase === 'before' && !this.hasShownStartAlert) {
        this.showSaleStartAlert();
        this.hasShownStartAlert = true;
        // Reset countdown to show transition
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        this.nextCountdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
    } else {
      // Phase 3: Sale ended
      newPhase = 'ended';
      distance = 0;

      // Show end alert only once when transitioning from 'during' to 'ended'
      if (previousPhase === 'during' && !this.hasShownEndAlert) {
        this.showSaleEndAlert();
        this.hasShownEndAlert = true;
      }
    }

    this.salePhase = newPhase;

    if (distance <= 0) {
      this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      this.nextCountdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    const newCountdown: CountdownTime = {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    };

    this.nextCountdown = newCountdown;

    // Trigger flip animations for changed values
    if (newCountdown.days !== this.countdown.days) {
      this.triggerFlip('days');
    }
    if (newCountdown.hours !== this.countdown.hours) {
      this.triggerFlip('hours');
    }
    if (newCountdown.minutes !== this.countdown.minutes) {
      this.triggerFlip('minutes');
    }
    if (newCountdown.seconds !== this.countdown.seconds) {
      this.triggerFlip('seconds');
    }

    setTimeout(() => {
      this.countdown = newCountdown;
    }, this.FLIP_ANIMATION_DELAY);
  }

  private triggerFlip(unit: 'days' | 'hours' | 'minutes' | 'seconds'): void {
    this.flipStates[unit] = true;
    setTimeout(() => {
      this.flipStates[unit] = false;
    }, this.FLIP_ANIMATION_DURATION);
  }

  // Handle scroll position changes from directive
  handleScrollChange(isInView: boolean): void {
    if (!this.showSection || !this.extendBackground) {
      return;
    }
    if (isInView) {
      document.body.classList.add('diwali-background-active');
    } else {
      document.body.classList.remove('diwali-background-active');
    }
  }

  getPlatformSaleType() {
  this.platformSaleService.getActivePlatFormSale().subscribe((res: any) => {
    let platFormActiveSaleList = res?.data?.sales;
    this.commonService.saleDataUpdate(platFormActiveSaleList);
  });
  }

  get isVisible(): boolean {
    return this.showSection;
  }

  onClickSearch(event: any): void {
    this.commonService.selectedSearchInputValue = event;
    this.commonService.sendSearchDataUrl(this.commonService.selectedSearchInputValue, "search");
  }

  onChangeSearchValue(event: any): void {
    this.commonService.selectedSearchInputValue = event?.target?.value;
    this.commonService.sendSearchDataUrl(this.commonService.selectedSearchInputValue, "search");
  }

  private showSaleStartAlert(): void {
    // this.getPlatformSaleType();
  }

  private showSaleEndAlert(): void {
    // this.getPlatformSaleType();
  }

  getCountdownLabel(): string {
    switch (this.salePhase) {
      case 'before':
        return 'Sale starts in:';
      case 'during':
        return 'Sale ends in:';
      case 'ended':
        return 'Sale has ended';
      default:
        return '';
    }
  }
}
